import React, { useEffect, useState } from 'react';
import { api, mutate, uploadFile } from '../shared/api';
import { DateRangeFilter, computeRangeForPreset, type DateRange } from '../shared/DateRangeFilter';
import { readUrlParam, useUrlSync } from '../shared/urlState';

type Locale = 'hr' | 'en';
type RevRow = { txn_date?: string; month?: string; dispensed: number; access_fees: number; dcc: number; interchange: number; total_revenue: number };
type SettleRow = { type: string; total: number; cf_settlement_total: number; diff: number; rolling_diff_last: number; rows: number };
type SettleDailyRow = { txn_date: string; settlement_type: string; total: number; cf_settlement_total: number; diff: number; rolling_diff: number };
type SettlementResp = {
    range: { from: string | null; to: string | null };
    by_type: SettleRow[];
    meta: { filtered_rows: number; total_rows_in_db: number; daily_returned: number };
    daily: SettleDailyRow[];
};
type FttRow = { txn_at: string | null; terminal_id: string; brand: string; country: string; buy_sell: string; currency: string; amount: number; contra_ccy: string; contra_amount: number; client_markup: number; margin_eur: number | null; status: string };
type Overview = {
    revenue: { kpis: { month_total: number; ytd_total: number; month_dispensed: number; last_date: string | null }; monthly: RevRow[]; daily_recent: RevRow[] };
    settlement: { by_type: SettleRow[] };
    ftt: { summary: { count: number; settled: number; declined: number; cancelled: number; other: number; total_margin_eur: number; total_amount_eur: number }; by_status: Array<{ status: string; count: number; margin_eur: number }>; recent: FttRow[] };
    meta: { revenue_rows: number; settlement_rows: number; ftt_rows: number; asp17_rows: number; asp21_rows: number; trueup_rows: number };
};

type NetSettlementResp = {
    range: { from: string; to: string };
    kpi: { month: string; total_revenue: number; total_deductions: number; net_settlement: number; paid_to_pbz: number; manual_count: number; total_count: number };
    monthly: Array<{ ym: string; total_revenue: number; net_settlement: number }>;
    daily: Array<{
        date: string; revenue: number; dispensed: number;
        deductions_estimated: number; net_estimated: number; paid_to_pbz: number; locked: boolean;
        revenue_paycorp: number | null; deductions_paycorp: number | null; net_manual: number | null; manual_entered: boolean;
    }>;
};
type TrueupResp = {
    rows: Array<{
        month: string; processing_fees_estimated: number; scheme_fees_estimated: number; interchange_fees_estimated: number;
        processing_fees_actual: number | null; scheme_fees_actual: number | null; interchange_fees_actual: number | null;
        cost_deduction_actual: number | null; true_up_adjustment_eur: number | null; truped_at: string | null;
    }>;
};
type TransactionsResp = {
    range: { from: string; to: string };
    pagination: { page: number; per_page: number; total: number };
    rows: Array<{
        id: number; tran_datetime: string | null; terminal_code: string; terminal_atm_id: number | null;
        association: string | null; region: string | null; cit_branch: string | null; tran_type: string | null;
        final_amount_eur: number | null; amount_to_settle_eur: number | null; access_fee: number | null;
        rsp_code: string | null; dcc_n_y: string | null; atm_name: string | null;
    }>;
    top_terminals_30d: Array<{ terminal_code: string; atm_name: string | null; cnt: number; total_eur: number }>;
};
type ChargebacksResp = {
    kpi: { open: number; resolved: number; total_30d: number };
    rows: Array<{
        id: number; exception_date: string | null; tran_datetime: string | null; acquirer_ref_nr: string | null;
        case_number: string | null; terminal_code: string | null; terminal_atm_id: number | null; card_assoc: string | null;
        txn_amount: number | null; txn_cur: string | null; status: string; resolved_at: string | null;
        resolved_note: string | null; alarm_task_id: number | null; atm_name: string | null;
    }>;
};
type IntakeStatusResp = {
    counts: { success: number; pending: number; total: number };
    last_imports: Array<{
        id: number; filename: string; sender_email: string | null; subject: string | null;
        classifier_guess: string | null; classifier_confidence: number | null;
        status: string; import_status: string | null; import_finished_at: string | null; created_at: string;
    }>;
};

type TabId = 'overview' | 'yoy' | 'net' | 'trueup' | 'transactions' | 'chargebacks' | 'settlement' | 'ftt';

type YoYMonthVal = { revenue: number | null; gross_profit: number | null; operating_result: number | null };
type YoYResp = {
    year: number;
    prev_year: number;
    kpi: {
        curr_year: number; prev_year: number;
        act_curr: { revenue: number; gross_profit: number };
        fc_curr: { revenue: number; gross_profit: number };
        act_prev: { revenue: number; gross_profit: number };
        yoy_revenue_pct: number | null;
        yoy_gp_pct: number | null;
        fc_act_revenue_pct: number | null;
    };
    monthly: Array<{ month: number; act_curr: YoYMonthVal; fc_curr: YoYMonthVal; act_prev: YoYMonthVal }>;
    daily: Array<{ date: string; act: number; fc: number | null; prev_year_act: number | null }>;
};

const eur = (n: number, d = 0) => new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR', minimumFractionDigits: d, maximumFractionDigits: d }).format(Number.isFinite(n) ? n : 0);
const num = (n: number, d = 0) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number.isFinite(n) ? n : 0);
const GOLD = 'var(--ad-acc)';
const BROWN = 'var(--ad-ink)';

const SETTLE_LABEL: Record<string, [string, string]> = {
    dispensed: ['Isplaćeno (Dispensed)', 'Dispensed'],
    af: ['Access Fees', 'Access Fees'],
    dcc: ['DCC (konverzija valute)', 'DCC'],
    ic: ['Interchange', 'Interchange'],
};

function statusColor(s: string): string {
    const x = s.toLowerCase();
    if (x.includes('settl')) return '#1f7a36';
    if (x.includes('declin') || x.includes('fail')) return '#b14040';
    if (x.includes('cancel')) return '#9a6b12';
    return '#555';
}

function MonthlyBars({ rows, locale, valueKey = 'total_revenue' }: { rows: Array<{ month?: string; ym?: string; total_revenue?: number; net_settlement?: number; [k: string]: unknown }>; locale: Locale; valueKey?: 'total_revenue' | 'net_settlement' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const data = rows.slice(-24);
    const max = Math.max(1, ...data.map((r) => Number(r[valueKey] ?? 0)));
    const W = Math.max(320, data.length * 34);
    const H = 180;
    const bw = W / Math.max(1, data.length);
    if (data.length === 0) return <p className="muted">{t('Nema podataka.', 'No data.')}</p>;
    return (
        <div style={{ overflowX: 'auto' }}>
            <svg width={W} height={H + 26} role="img" style={{ display: 'block' }}>
                {data.map((r, i) => {
                    const v = Number(r[valueKey] ?? 0);
                    const h = Math.round((H - 8) * v / max);
                    const x = i * bw;
                    const lbl = ((r.month as string | undefined) || (r.ym as string | undefined) || '').slice(2);
                    return (
                        <g key={i}>
                            <title>{`${r.month ?? r.ym}: ${eur(v)}`}</title>
                            <rect x={x + bw * 0.12} y={H - h} width={bw * 0.76} height={h} rx={3} fill={GOLD} opacity={0.88} />
                            {i % 3 === 0 ? <text x={x + bw / 2} y={H + 16} textAnchor="middle" fontSize="9" fill="#8a6817">{lbl}</text> : null}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

const TABS: Array<{ id: TabId; emoji: string; hr: string; en: string }> = [
    { id: 'overview', emoji: '💰', hr: 'Pregled', en: 'Overview' },
    { id: 'yoy', emoji: '📊', hr: 'YoY + Forecast', en: 'YoY + Forecast' },
    { id: 'net', emoji: '💵', hr: 'Net Settlement', en: 'Net Settlement' },
    { id: 'trueup', emoji: '📅', hr: 'Mjesečno usklađenje', en: 'Monthly true-up' },
    { id: 'settlement', emoji: '🏦', hr: 'Settlement detalj', en: 'Settlement detail' },
    { id: 'transactions', emoji: '💳', hr: 'Transakcije', en: 'Transactions' },
    { id: 'chargebacks', emoji: '⚠️', hr: 'Chargebacks', en: 'Chargebacks' },
    { id: 'ftt', emoji: '💱', hr: 'FX / FTT', en: 'FX / FTT' },
];

export function MonestiqFinanceModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Overview | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [importMsg, setImportMsg] = useState('');
    const [tab, setTab] = useState<TabId>(readUrlParam('tab', 'overview') as TabId);
    useUrlSync('tab', tab, tab === 'overview');

    // Per-tab lazy data
    const [netData, setNetData] = useState<NetSettlementResp | null>(null);
    const [trueupData, setTrueupData] = useState<TrueupResp | null>(null);
    const [transactionsData, setTransactionsData] = useState<TransactionsResp | null>(null);
    const [chargebacksData, setChargebacksData] = useState<ChargebacksResp | null>(null);
    const [settlementData, setSettlementData] = useState<SettlementResp | null>(null);
    const [yoyData, setYoyData] = useState<YoYResp | null>(null);
    const [yoyYear, setYoyYear] = useState<number>(new Date().getFullYear());
    const [intakeStatus, setIntakeStatus] = useState<IntakeStatusResp | null>(null);

    // v347.3 — per-tab date range filteri
    const [stRange, setStRange] = useState<DateRange>({ from: null, to: null }); // Sve (sva povijest)
    const [netRange, setNetRange] = useState<DateRange>(() => computeRangeForPreset('30d'));
    const [txRange, setTxRange] = useState<DateRange>(() => computeRangeForPreset('7d'));
    const [cbRange, setCbRange] = useState<DateRange>(() => computeRangeForPreset('30d'));

    const reload = React.useCallback(() => {
        setLoading(true);
        api<Overview>('/monestiq/finance/overview', token)
            .then((r) => { setData(r); setError(''); })
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token]);
    useEffect(() => { reload(); }, [reload]);

    // Intake status uvijek (mali payload, refresh svake 60s)
    useEffect(() => {
        if (!token) return;
        const load = () => api<IntakeStatusResp>('/monestiq/finance/intake-status', token).then(setIntakeStatus).catch(() => undefined);
        void load();
        const id = window.setInterval(() => { void load(); }, 60000);
        return () => window.clearInterval(id);
    }, [token]);

    // Lazy fetch per-tab — Trueup je bez range filtera (već je po mjesecu, fixed 36m view)
    useEffect(() => {
        if (!token) return;
        if (tab === 'trueup' && !trueupData) {
            api<TrueupResp>('/monestiq/finance/monthly-trueup', token).then(setTrueupData).catch(() => undefined);
        }
    }, [tab, token, trueupData]);

    // v347.3 — refetch on range change (i na tab switch). Helper koji gradi query string.
    const rangeQs = React.useCallback((r: DateRange): string => {
        const p = new URLSearchParams();
        if (r.from) p.set('from', r.from);
        if (r.to) p.set('to', r.to);
        const s = p.toString();
        return s ? '?' + s : '';
    }, []);

    useEffect(() => {
        if (!token || tab !== 'settlement') return;
        api<SettlementResp>('/monestiq/finance/settlement' + rangeQs(stRange), token).then(setSettlementData).catch(() => undefined);
    }, [tab, token, stRange.from, stRange.to, rangeQs]);

    useEffect(() => {
        if (!token || tab !== 'net') return;
        api<NetSettlementResp>('/monestiq/finance/net-settlement' + rangeQs(netRange), token).then(setNetData).catch(() => undefined);
    }, [tab, token, netRange.from, netRange.to, rangeQs]);

    useEffect(() => {
        if (!token || tab !== 'transactions') return;
        api<TransactionsResp>('/monestiq/finance/transactions' + rangeQs(txRange), token).then(setTransactionsData).catch(() => undefined);
    }, [tab, token, txRange.from, txRange.to, rangeQs]);

    useEffect(() => {
        if (!token || tab !== 'chargebacks') return;
        api<ChargebacksResp>('/monestiq/finance/chargebacks' + rangeQs(cbRange), token).then(setChargebacksData).catch(() => undefined);
    }, [tab, token, cbRange.from, cbRange.to, rangeQs]);

    useEffect(() => {
        if (!token || tab !== 'yoy') return;
        api<YoYResp>(`/monestiq/finance/yoy?year=${yoyYear}`, token).then(setYoyData).catch(() => undefined);
    }, [tab, token, yoyYear]);

    const onImport = async (fileList: FileList | null) => {
        if (!token || !fileList || fileList.length === 0) return;
        setImporting(true); setImportMsg('');
        try {
            const fd = new FormData();
            Array.from(fileList).forEach((f) => fd.append('files[]', f));
            const r = await uploadFile<{ ok: boolean; saved: string[]; output: string }>('/monestiq/finance/import', token, fd);
            setImportMsg((r.output || t('Uvoz gotov.', 'Import done.')) + `\n${t('Datoteke', 'Files')}: ${(r.saved ?? []).join(', ')}`);
            reload();
        } catch (e) { setImportMsg('⚠ ' + (e instanceof Error ? e.message : 'API')); }
        finally { setImporting(false); }
    };

    const saveNetManual = async (date: string, field: 'total_revenue_paycorp' | 'total_deductions_paycorp' | 'net_settlement_manual', value: string) => {
        if (!token) return;
        const num = value.trim() === '' ? null : Number(value.replace(',', '.'));
        if (num !== null && !Number.isFinite(num)) { alert(t('Nevažeći broj', 'Invalid number')); return; }
        try {
            await mutate(`/monestiq/finance/net-settlement/${date}`, token, 'PATCH', { [field]: num });
            const r = await api<NetSettlementResp>('/monestiq/finance/net-settlement' + rangeQs(netRange), token);
            setNetData(r);
        } catch (e) { alert(String(e)); }
    };
    const clearNetManual = async (date: string) => {
        if (!token) return;
        if (!confirm(t('Obriši ručne brojeve i otključaj auto-procjenu za '+date+'?', 'Clear manual numbers and unlock auto-estimate for '+date+'?'))) return;
        try {
            await mutate(`/monestiq/finance/net-settlement/${date}`, token, 'PATCH', { clear: true });
            const r = await api<NetSettlementResp>('/monestiq/finance/net-settlement' + rangeQs(netRange), token);
            setNetData(r);
        } catch (e) { alert(String(e)); }
    };

    const resolveChargeback = async (id: number) => {
        if (!token) return;
        const note = prompt(t('Napomena (opcionalno):', 'Note (optional):'), '');
        if (note === null) return;
        try {
            await mutate(`/monestiq/finance/chargebacks/${id}/resolve`, token, 'POST', { note: note || null });
            // Reload chargebacks
            const r = await api<ChargebacksResp>('/monestiq/finance/chargebacks' + rangeQs(cbRange), token);
            setChargebacksData(r);
        } catch (e) { alert(String(e)); }
    };

    const kpis = data?.revenue.kpis;
    const ftt = data?.ftt;
    const empty = data && (data.meta.revenue_rows + data.meta.settlement_rows + data.meta.ftt_rows) === 0;

    const tile = (label: string, value: string, sub?: string, color = BROWN) => (
        <div style={{ flex: '1 1 160px', minWidth: 150, background: 'linear-gradient(180deg,#fffdf6,var(--ad-bg))', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: '14px 16px' }}>
            <div className="muted" style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color, marginTop: 4 }}>{value}</div>
            {sub ? <div className="muted" style={{ fontSize: '0.76rem', marginTop: 2 }}>{sub}</div> : null}
        </div>
    );

    const fmtDt = (s: string | null | undefined) => {
        if (!s) return '—';
        const m = /^(\d{4})-(\d{2})-(\d{2})[T ]?(\d{2}:\d{2})?/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}.${m[4] ? ' ' + m[4] : ''}` : s;
    };
    const fmtDate = (s: string | null | undefined) => {
        if (!s) return '—';
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1]}.` : s;
    };

    return (
        <section className="card admin-block">
            {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ marginBottom: 8 }}>← {t('Natrag na Monestiq', 'Back to Monestiq')}</button> : null}
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📊</div>
                <div className="bi-module-hero-text">
                    <h1>20.1 · {t('Pregled financija', 'Financial overview')}</h1>
                    <p className="muted">{t('Monestiq — prihodi po danu, Net Settlement, transakcije, chargebackovi. Auto-intake preko obrada@aurodomus.eu (Hrvoje forward).', 'Monestiq — daily revenue, Net Settlement, transactions, chargebacks. Auto-intake via obrada@aurodomus.eu (Hrvoje forward).')}</p>
                </div>
            </div>

            {/* Intake status panel — uvijek vidljiv */}
            {intakeStatus ? (
                <div style={{ marginBottom: 12, padding: '8px 14px', background: 'rgba(31,122,54,0.06)', border: '1px solid rgba(31,122,54,0.3)', borderRadius: 8, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <strong style={{ color: '#1f7a36' }}>📥 {t('Intake (zadnjih 7 dana)', 'Intake (last 7 days)')}:</strong>
                    <span>✓ {intakeStatus.counts.success} {t('uspješno', 'success')}</span>
                    <span style={{ color: intakeStatus.counts.pending > 0 ? '#9a6b12' : '#888' }}>⏳ {intakeStatus.counts.pending} {t('čeka', 'pending')}</span>
                    {intakeStatus.last_imports[0] ? (
                        <span style={{ marginLeft: 'auto', color: '#666' }}>
                            {t('Zadnji', 'Latest')}: <strong>{intakeStatus.last_imports[0].filename}</strong> · {fmtDt(intakeStatus.last_imports[0].created_at)}
                        </span>
                    ) : null}
                </div>
            ) : null}

            {/* Upload — globalni, iznad tabova */}
            <div style={{ border: '1px dashed rgba(var(--ad-soft-rgb),0.5)', borderRadius: 12, padding: '10px 14px', marginBottom: 12, background: 'rgba(var(--ad-acc-rgb),0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <strong style={{ color: BROWN, fontSize: '0.86rem' }}>⬆ {t('Ručni uvoz', 'Manual upload')}</strong>
                    <label className="secondary compact" style={{ cursor: importing ? 'default' : 'pointer', fontSize: '0.78rem', opacity: importing ? 0.6 : 1 }}>
                        📁 {t('Odaberi datoteke', 'Choose files')}
                        <input type="file" multiple accept=".xlsx,.xls,.pdf" disabled={importing} onChange={(e) => { void onImport(e.target.files); e.currentTarget.value = ''; }} style={{ display: 'none' }} />
                    </label>
                    {importing ? <span className="muted" style={{ fontSize: '0.78rem' }}>⏳ {t('Uvozim…', 'Importing…')}</span> : null}
                    <span className="muted" style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>{t('Croatia Settlement Summary · ASP17 · ASP21 · FTT', 'Croatia Settlement Summary · ASP17 · ASP21 · FTT')}</span>
                </div>
                {importMsg ? <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.72rem', background: '#fffdf6', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: '6px 8px', marginTop: 6, marginBottom: 0, maxHeight: 130, overflow: 'auto' }}>{importMsg}</pre> : null}
            </div>

            {/* Tab nav */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap', borderBottom: '2px solid rgba(var(--ad-soft-rgb),0.2)', paddingBottom: 0 }}>
                {TABS.map((tDef) => (
                    <button key={tDef.id} type="button" onClick={() => setTab(tDef.id)}
                        style={{
                            padding: '8px 14px', background: tab === tDef.id ? GOLD : 'transparent', color: tab === tDef.id ? '#fff' : BROWN,
                            border: '1px solid var(--ad-acc)', borderBottom: tab === tDef.id ? `1px solid ${GOLD}` : '1px solid var(--ad-acc)',
                            borderRadius: '8px 8px 0 0', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700,
                            marginBottom: -2, position: 'relative', top: 2,
                        }}>
                        {tDef.emoji} {locale === 'en' ? tDef.en : tDef.hr}
                    </button>
                ))}
            </div>

            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {error ? <p className="error">{error}</p> : null}
            {empty ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nema uvezenih podataka — uvezi izvještaje gumbom gore (⬆).', 'No imported data yet — upload reports above (⬆).')}</p> : null}

            {/* Tab: Overview (originalna 3-sekcija) */}
            {tab === 'overview' && data && !empty ? (
                <>
                    <h2 style={{ color: BROWN, fontSize: '1.1rem', marginTop: 6 }}>💰 {t('Prihod (ATM)', 'Revenue (ATM)')}</h2>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                        {tile(t('Mjesec — ukupno', 'Month — total'), eur(kpis?.month_total ?? 0), kpis?.last_date ? t('do', 'to') + ' ' + kpis.last_date : undefined, '#1f7a36')}
                        {tile('YTD', eur(kpis?.ytd_total ?? 0))}
                        {tile(t('Mjesec — isplaćeno', 'Month — dispensed'), eur(kpis?.month_dispensed ?? 0))}
                    </div>
                    <div style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: 14, marginBottom: 18 }}>
                        <strong style={{ color: BROWN, fontSize: '0.9rem' }}>{t('Ukupan prihod po mjesecu', 'Total revenue by month')}</strong>
                        <MonthlyBars rows={data.revenue.monthly} locale={locale} valueKey="total_revenue" />
                    </div>
                </>
            ) : null}

            {/* Tab: YoY + Forecast (v348) */}
            {tab === 'yoy' ? (
                <>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, padding: 10, background: 'var(--ad-bg)', borderRadius: 8 }}>
                        <strong style={{ color: BROWN, fontSize: '0.86rem' }}>📊 {t('YoY + Forecast usporedba', 'YoY + Forecast comparison')}</strong>
                        <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: BROWN }}>{t('Godina', 'Year')}:</span>
                        <select value={yoyYear} onChange={(e) => setYoyYear(Number(e.target.value))}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', fontSize: '0.84rem' }}>
                            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    {yoyData ? (
                        <>
                            {/* KPI tile-ovi */}
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                                {tile(
                                    `${t('Promet ACT', 'Revenue ACT')} ${yoyData.year}`,
                                    eur(yoyData.kpi.act_curr.revenue, 0),
                                    yoyData.kpi.yoy_revenue_pct !== null
                                        ? `${yoyData.kpi.yoy_revenue_pct > 0 ? '+' : ''}${yoyData.kpi.yoy_revenue_pct}% YoY`
                                        : undefined,
                                    yoyData.kpi.yoy_revenue_pct !== null && yoyData.kpi.yoy_revenue_pct >= 0 ? '#1f7a36' : '#b14040',
                                )}
                                {tile(
                                    `${t('Promet FC', 'Revenue FC')} ${yoyData.year}`,
                                    eur(yoyData.kpi.fc_curr.revenue, 0),
                                    yoyData.kpi.fc_act_revenue_pct !== null
                                        ? `ACT ${yoyData.kpi.fc_act_revenue_pct > 0 ? '+' : ''}${yoyData.kpi.fc_act_revenue_pct}% vs FC`
                                        : undefined,
                                    GOLD,
                                )}
                                {tile(
                                    `${t('Promet ACT', 'Revenue ACT')} ${yoyData.prev_year}`,
                                    eur(yoyData.kpi.act_prev.revenue, 0),
                                    undefined,
                                    '#666',
                                )}
                                {tile(
                                    `${t('RUC ACT', 'Gross Profit ACT')} ${yoyData.year}`,
                                    eur(yoyData.kpi.act_curr.gross_profit, 0),
                                    yoyData.kpi.yoy_gp_pct !== null
                                        ? `${yoyData.kpi.yoy_gp_pct > 0 ? '+' : ''}${yoyData.kpi.yoy_gp_pct}% YoY`
                                        : undefined,
                                    yoyData.kpi.yoy_gp_pct !== null && yoyData.kpi.yoy_gp_pct >= 0 ? '#1f7a36' : '#b14040',
                                )}
                                {tile(
                                    `${t('RUC FC', 'Gross Profit FC')} ${yoyData.year}`,
                                    eur(yoyData.kpi.fc_curr.gross_profit, 0),
                                    undefined,
                                    GOLD,
                                )}
                                {tile(
                                    `${t('RUC ACT', 'Gross Profit ACT')} ${yoyData.prev_year}`,
                                    eur(yoyData.kpi.act_prev.gross_profit, 0),
                                    undefined,
                                    '#666',
                                )}
                            </div>

                            {/* Mjesečna usporedba */}
                            <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, marginBottom: 14 }}>
                                <table className="admin-user-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                                    <thead><tr>
                                        <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>{t('Mjesec', 'Month')}</th>
                                        <th colSpan={3} style={{ textAlign: 'center', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>{t('Promet', 'Revenue')}</th>
                                        <th colSpan={3} style={{ textAlign: 'center', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>{t('RUC (Gross Profit)', 'Gross Profit (RUC)')}</th>
                                    </tr><tr>
                                        <th style={{ textAlign: 'right' }}>ACT {yoyData.year}</th>
                                        <th style={{ textAlign: 'right' }}>FC {yoyData.year}</th>
                                        <th style={{ textAlign: 'right', color: '#888' }}>ACT {yoyData.prev_year}</th>
                                        <th style={{ textAlign: 'right' }}>ACT {yoyData.year}</th>
                                        <th style={{ textAlign: 'right' }}>FC {yoyData.year}</th>
                                        <th style={{ textAlign: 'right', color: '#888' }}>ACT {yoyData.prev_year}</th>
                                    </tr></thead>
                                    <tbody>
                                        {yoyData.monthly.map((mo) => (
                                            <tr key={mo.month}>
                                                <td style={{ fontWeight: 600 }}>{String(mo.month).padStart(2, '0')}/{yoyData.year}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{mo.act_curr.revenue !== null ? eur(mo.act_curr.revenue, 0) : '—'}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: GOLD }}>{mo.fc_curr.revenue !== null ? eur(mo.fc_curr.revenue, 0) : '—'}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{mo.act_prev.revenue !== null ? eur(mo.act_prev.revenue, 0) : '—'}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{mo.act_curr.gross_profit !== null ? eur(mo.act_curr.gross_profit, 0) : '—'}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: GOLD }}>{mo.fc_curr.gross_profit !== null ? eur(mo.fc_curr.gross_profit, 0) : '—'}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{mo.act_prev.gross_profit !== null ? eur(mo.act_prev.gross_profit, 0) : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <p className="muted" style={{ fontSize: '0.72rem' }}>
                                {t(
                                    'Izvor P&L (Revenue, COGS, Gross Profit, OPEX, Operating Result): Hrvojev master Operating Result xlsx. Dnevni Revenue iz bi_monestiq_revenue_daily (Tsitsi-jeve Revenue by Transaction Date). Forecast (FC) iz MQ-Revenue-FC per day.xlsx.',
                                    'P&L source (Revenue, COGS, Gross Profit, OPEX, Operating Result): Hrvoje\'s Operating Result master xlsx. Daily Revenue from bi_monestiq_revenue_daily (Tsitsi\'s Revenue by Transaction Date). Forecast (FC) from MQ-Revenue-FC per day.xlsx.',
                                )}
                            </p>
                        </>
                    ) : (
                        <p className="muted">{t('Učitavam…', 'Loading…')}</p>
                    )}
                </>
            ) : null}

            {/* Tab: Net Settlement */}
            {tab === 'net' ? (
                <>
                    <DateRangeFilter value={netRange} onChange={setNetRange} locale={locale} label={t('Razdoblje (dnevna lista)', 'Range (daily list)')} />
                {netData ? (
                    <>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                            {tile(t('Mjesec Revenue', 'Month Revenue'), eur(netData.kpi.total_revenue, 2))}
                            {tile(t('Mjesec Deductions (procjena)', 'Month Deductions (est.)'), eur(netData.kpi.total_deductions, 2), undefined, '#b14040')}
                            {tile(t('Net Settlement (procjena)', 'Net Settlement (est.)'), eur(netData.kpi.net_settlement, 2), undefined, '#1f7a36')}
                            {tile(t('Isplaćeno PBZ', 'Paid to PBZ'), eur(netData.kpi.paid_to_pbz, 2), undefined, GOLD)}
                        </div>
                        <div style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: 14, marginBottom: 18 }}>
                            <strong style={{ color: BROWN, fontSize: '0.9rem' }}>{t('Net Settlement po mjesecu (procjena)', 'Net Settlement by month (estimated)')}</strong>
                            <MonthlyBars rows={netData.monthly} locale={locale} valueKey="net_settlement" />
                        </div>
                        <div style={{ marginBottom: 6, padding: 8, background: 'rgba(var(--ad-acc-rgb),0.08)', borderRadius: 6, fontSize: '0.78rem', color: BROWN, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <strong>✍️ {t('Ručni upis', 'Manual entry')}:</strong>
                            <span>{netData.kpi.manual_count} {t('od', 'of')} {netData.kpi.total_count} {t('dana ručno upisano', 'days manually entered')}</span>
                            <span className="muted" style={{ marginLeft: 'auto', fontStyle: 'italic' }}>{t('Klikni broj u tablici → upiši točan iznos iz Paycorp slike → enter/blur sprema. Ručni brojevi imaju prioritet nad procjenom.', 'Click number in table → type exact value from Paycorp image → enter/blur saves. Manual values override estimate.')}</span>
                        </div>
                        <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.84rem' }}>
                                <thead><tr>
                                    <th>{t('Datum', 'Date')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Revenue (xls)', 'Revenue (xls)')}</th>
                                    <th style={{ textAlign: 'right', background: 'rgba(var(--ad-acc-rgb),0.12)' }}>{t('Revenue (Paycorp ✍)', 'Revenue (Paycorp ✍)')}</th>
                                    <th style={{ textAlign: 'right', background: 'rgba(var(--ad-acc-rgb),0.12)' }}>{t('Deductions (Paycorp ✍)', 'Deductions (Paycorp ✍)')}</th>
                                    <th style={{ textAlign: 'right', background: 'rgba(var(--ad-acc-rgb),0.12)' }}>{t('Net (Paycorp ✍)', 'Net (Paycorp ✍)')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('PBZ', 'PBZ')}</th>
                                    <th>{t('Status', 'Status')}</th>
                                </tr></thead>
                                <tbody>
                                    {netData.daily.map((d) => (
                                        <tr key={d.date} style={{ background: d.manual_entered ? 'rgba(31,122,54,0.04)' : 'transparent' }}>
                                            <td style={{ fontFamily: 'ui-monospace, monospace' }}>{fmtDate(d.date)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: '#888' }}>{eur(d.revenue, 2)}</td>
                                            <td style={{ textAlign: 'right', padding: '2px 4px', background: 'rgba(var(--ad-acc-rgb),0.04)' }}>
                                                <input type="text" defaultValue={d.revenue_paycorp !== null ? d.revenue_paycorp.toFixed(2) : ''}
                                                    placeholder="—" onBlur={(e) => { if (e.target.value !== (d.revenue_paycorp?.toFixed(2) ?? '')) void saveNetManual(d.date, 'total_revenue_paycorp', e.target.value); }}
                                                    style={{ width: '100%', border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: '0.84rem', padding: '4px 6px', color: d.revenue_paycorp !== null ? '#444' : '#bbb' }} />
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '2px 4px', background: 'rgba(var(--ad-acc-rgb),0.04)' }}>
                                                <input type="text" defaultValue={d.deductions_paycorp !== null ? d.deductions_paycorp.toFixed(2) : ''}
                                                    placeholder="—" onBlur={(e) => { if (e.target.value !== (d.deductions_paycorp?.toFixed(2) ?? '')) void saveNetManual(d.date, 'total_deductions_paycorp', e.target.value); }}
                                                    style={{ width: '100%', border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: '0.84rem', padding: '4px 6px', color: d.deductions_paycorp !== null ? '#b14040' : '#bbb' }} />
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '2px 4px', background: 'rgba(var(--ad-acc-rgb),0.04)' }}>
                                                <input type="text" defaultValue={d.net_manual !== null ? d.net_manual.toFixed(2) : ''}
                                                    placeholder="—" onBlur={(e) => { if (e.target.value !== (d.net_manual?.toFixed(2) ?? '')) void saveNetManual(d.date, 'net_settlement_manual', e.target.value); }}
                                                    style={{ width: '100%', border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: '0.84rem', padding: '4px 6px', color: d.net_manual !== null ? '#1f7a36' : '#bbb' }} />
                                            </td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: GOLD }}>{eur(d.paid_to_pbz, 2)}</td>
                                            <td>
                                                {d.locked ? <span title={t('Mjesečni true-up primijenjen', 'Monthly true-up applied')} style={{ background: '#1f7a36', color: '#fff', padding: '1px 5px', borderRadius: 4, fontSize: '0.7rem' }}>🔒 truepup</span>
                                                : d.manual_entered ? <button type="button" onClick={() => void clearNetManual(d.date)} title={t('Obriši ručne brojeve', 'Clear manual')} style={{ background: 'var(--ad-acc)', color: '#fff', padding: '1px 5px', borderRadius: 4, fontSize: '0.7rem', border: 0, cursor: 'pointer' }}>✍ {t('ručno', 'manual')}</button>
                                                : <span style={{ color: '#aaa', fontSize: '0.7rem' }}>📊 {t('procjena', 'estimated')}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="muted" style={{ fontSize: '0.72rem', marginTop: 8, fontStyle: 'italic' }}>
                            {t('✍ Ručno = Hrvoje upisao iz Paycorp slike (točan broj). 📊 Procjena = auto iz xls-a. 🔒 truepup = mjesečna fakturira lockirala.', '✍ Manual = Hrvoje typed from Paycorp image (exact). 📊 Estimate = auto from xls. 🔒 truepup = monthly invoice locked.')}
                        </p>
                    </>
                ) : <p className="muted">{t('Učitavam…', 'Loading…')}</p>}
                </>
            ) : null}

            {/* Tab: Trueup */}
            {tab === 'trueup' ? (
                trueupData ? (
                    trueupData.rows.length === 0 ? (
                        <p className="muted" style={{ fontStyle: 'italic' }}>{t('Još nema mjesečnih usklađenja. Stiže preko Monestiq_Monthly_Invoice xlsx.', 'No monthly trueups yet. Arrives via Monestiq_Monthly_Invoice xlsx.')}</p>
                    ) : (
                        <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.84rem' }}>
                                <thead><tr>
                                    <th>{t('Mjesec', 'Month')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Σ Proc procjena', 'Σ Proc est.')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Σ Proc stvarno', 'Σ Proc actual')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Cost Deduction', 'Cost Deduction')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('True-up Δ', 'True-up Δ')}</th>
                                    <th>{t('Datum', 'Date')}</th>
                                </tr></thead>
                                <tbody>
                                    {trueupData.rows.map((r) => (
                                        <tr key={r.month}>
                                            <td style={{ fontWeight: 700 }}>{r.month}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(r.processing_fees_estimated, 2)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{r.processing_fees_actual !== null ? eur(r.processing_fees_actual, 2) : <span className="muted">⏳</span>}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{r.cost_deduction_actual !== null ? eur(r.cost_deduction_actual, 2) : '—'}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: r.true_up_adjustment_eur !== null && r.true_up_adjustment_eur > 0 ? '#1f7a36' : (r.true_up_adjustment_eur !== null && r.true_up_adjustment_eur < 0 ? '#b14040' : '#444') }}>{r.true_up_adjustment_eur !== null ? eur(r.true_up_adjustment_eur, 2) : '—'}</td>
                                            <td>{r.truped_at ? fmtDt(r.truped_at) : <span className="muted">⏳</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : <p className="muted">{t('Učitavam…', 'Loading…')}</p>
            ) : null}

            {/* Tab: Settlement detalj — original 4-type tablica + daily breakdown s date filterom (v347.3) */}
            {tab === 'settlement' && data && !empty ? (
                <>
                    <DateRangeFilter value={stRange} onChange={setStRange} locale={locale} />
                    {settlementData ? (
                        <div style={{ marginBottom: 8, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)' }}>
                            {t(
                                `Prikazano: ${num(settlementData.meta.filtered_rows)} od ${num(settlementData.meta.total_rows_in_db)} dana ukupno (rolling razlika je iz pune povijesti).`,
                                `Showing: ${num(settlementData.meta.filtered_rows)} of ${num(settlementData.meta.total_rows_in_db)} total days (rolling diff is from full history).`,
                            )}
                        </div>
                    ) : (
                        <div style={{ marginBottom: 8, fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)' }}>{t('Učitavanje…', 'Loading…')}</div>
                    )}
                <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                    <table className="admin-user-table" style={{ width: '100%', fontSize: '0.86rem' }}>
                        <thead><tr>
                            <th>{t('Vrsta', 'Type')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Obračun (billing)', 'Billing total')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Namireno (CF)', 'CF settled')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Razlika', 'Diff')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Rolling (zadnji)', 'Rolling (latest)')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Dana', 'Days')}</th>
                        </tr></thead>
                        <tbody>
                            {(settlementData?.by_type ?? data.settlement.by_type).map((s) => (
                                <tr key={s.type}>
                                    <td style={{ fontWeight: 600 }}>{t(...(SETTLE_LABEL[s.type] ?? [s.type, s.type]))}</td>
                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(s.total, 2)}</td>
                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(s.cf_settlement_total, 2)}</td>
                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: Math.abs(s.diff) > 0.005 ? '#b14040' : '#1f7a36' }}>{eur(s.diff, 2)}</td>
                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: 'rgba(0,0,0,0.55)' }}>{eur(s.rolling_diff_last, 2)}</td>
                                    <td style={{ textAlign: 'right', color: 'rgba(0,0,0,0.55)' }}>{num(s.rows)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {settlementData && settlementData.daily.length > 0 ? (
                    <details style={{ marginTop: 12, padding: '8px 12px', background: '#fffdf7', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 8 }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: BROWN, fontWeight: 600 }}>
                            📋 {t(`Dnevni detalj (prvih ${settlementData.daily.length})`, `Daily detail (first ${settlementData.daily.length})`)}
                        </summary>
                        <div className="table-wrap" style={{ marginTop: 8, maxHeight: 420, overflowY: 'auto' }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.78rem' }}>
                                <thead><tr>
                                    <th>{t('Datum', 'Date')}</th>
                                    <th>{t('Vrsta', 'Type')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Obračun', 'Billing')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Namireno', 'CF settled')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Razlika', 'Diff')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Rolling', 'Rolling')}</th>
                                </tr></thead>
                                <tbody>
                                    {settlementData.daily.map((d, i) => (
                                        <tr key={`${d.txn_date}-${d.settlement_type}-${i}`}>
                                            <td style={{ fontFamily: 'ui-monospace, monospace' }}>{d.txn_date}</td>
                                            <td>{t(...(SETTLE_LABEL[d.settlement_type] ?? [d.settlement_type, d.settlement_type]))}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(d.total, 2)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(d.cf_settlement_total, 2)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: Math.abs(d.diff) > 0.005 ? '#b14040' : '#1f7a36' }}>{eur(d.diff, 2)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: 'rgba(0,0,0,0.55)' }}>{eur(d.rolling_diff, 2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </details>
                ) : null}
                </>
            ) : null}

            {/* Tab: Transactions ASP17 */}
            {tab === 'transactions' ? (
                <>
                    <DateRangeFilter value={txRange} onChange={setTxRange} locale={locale} />
                {transactionsData ? (
                    transactionsData.pagination.total === 0 ? (
                        <div style={{ padding: 30, textAlign: 'center', background: 'var(--ad-bg)', borderRadius: 8, border: '1px dashed rgba(var(--ad-soft-rgb),0.4)' }}>
                            <div style={{ fontSize: '2rem', marginBottom: 8 }}>💳</div>
                            <strong style={{ color: BROWN, display: 'block', marginBottom: 6 }}>{t('Još nema ASP17 transakcija.', 'No ASP17 transactions yet.')}</strong>
                            <p className="muted" style={{ fontSize: '0.82rem', maxWidth: 540, margin: '0 auto' }}>
                                {t('Hrvoje forwarda ASP17-CF-HR-*.xls fajl s Paycorpa na obrada@aurodomus.eu. Sljedeći 1-min cron tick (bi:fetch-obrada-inbox) klasificira ga kao 20.monestiq.asp17 i parser uvodi transakcije (~800 redaka po danu).', 'Hrvoje forwards ASP17-CF-HR-*.xls from Paycorp to obrada@aurodomus.eu. Next 1-min cron (bi:fetch-obrada-inbox) classifies as 20.monestiq.asp17 and the parser imports transactions (~800 rows/day).')}
                            </p>
                        </div>
                    ) : (
                    <>
                        <div style={{ marginBottom: 12, padding: 10, background: 'var(--ad-bg)', borderRadius: 6 }}>
                            <strong style={{ color: BROWN, fontSize: '0.86rem' }}>🏆 {t('Top 10 terminala (30 dana)', 'Top 10 terminals (30d)')}</strong>
                            <table style={{ width: '100%', fontSize: '0.78rem', marginTop: 6 }}>
                                <thead><tr style={{ color: '#888' }}>
                                    <th style={{ textAlign: 'left' }}>{t('Terminal', 'Terminal')}</th>
                                    <th style={{ textAlign: 'left' }}>{t('Bankomat', 'ATM')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Transakcija', 'Transactions')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Promet (EUR)', 'Volume (EUR)')}</th>
                                </tr></thead>
                                <tbody>
                                    {transactionsData.top_terminals_30d.map((tt) => (
                                        <tr key={tt.terminal_code}>
                                            <td style={{ fontFamily: 'monospace' }}>{tt.terminal_code}</td>
                                            <td>{tt.atm_name ?? '—'}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{num(tt.cnt)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{eur(tt.total_eur, 2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.78rem' }}>
                                <thead><tr>
                                    <th>{t('Vrijeme', 'Time')}</th>
                                    <th>{t('Bankomat', 'ATM')}</th>
                                    <th>{t('Brand', 'Brand')}</th>
                                    <th>{t('Regija', 'Region')}</th>
                                    <th>{t('Tip', 'Type')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Fee', 'Fee')}</th>
                                    <th>{t('Resp', 'Resp')}</th>
                                </tr></thead>
                                <tbody>
                                    {transactionsData.rows.map((r) => (
                                        <tr key={r.id}>
                                            <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtDt(r.tran_datetime)}</td>
                                            <td>{r.atm_name ?? r.terminal_code}</td>
                                            <td>{r.association ?? '—'}</td>
                                            <td>{r.region ?? '—'}</td>
                                            <td>{r.tran_type ?? '—'}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{r.final_amount_eur !== null ? eur(r.final_amount_eur, 2) : '—'}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{r.access_fee !== null ? num(r.access_fee, 4) : '—'}</td>
                                            <td>{r.rsp_code ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                            {t('Prikazano', 'Showing')} {transactionsData.rows.length} {t('od', 'of')} {num(transactionsData.pagination.total)} {t('redaka', 'rows')}.
                            {' '}{t('Filter (terminal, brand, regija) i paginacija dolaze u sljedećoj iteraciji — koristi raspon datuma gore.', 'Filter (terminal, brand, region) and pagination next iteration — use date range above for now.')}
                        </p>
                    </>
                    )
                ) : <p className="muted">{t('Učitavam… (ASP17 može imati 800+ redaka po danu, prvi load par sekundi)', 'Loading… (ASP17 can have 800+ rows/day, first load takes a moment)')}</p>}
                </>
            ) : null}

            {/* Tab: Chargebacks ASP21 */}
            {tab === 'chargebacks' ? (
                <>
                    <DateRangeFilter value={cbRange} onChange={setCbRange} locale={locale} />
                {chargebacksData ? (
                    <>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                            {tile(t('Open', 'Open'), num(chargebacksData.kpi.open), undefined, chargebacksData.kpi.open > 0 ? '#b14040' : '#1f7a36')}
                            {tile(t('Resolved', 'Resolved'), num(chargebacksData.kpi.resolved), undefined, '#1f7a36')}
                            {tile(t('Ukupno (raspon)', 'Total (range)'), num(chargebacksData.kpi.total_30d))}
                        </div>
                        {chargebacksData.rows.length === 0 ? (
                            <div style={{ padding: 20, background: 'rgba(31,122,54,0.08)', borderRadius: 8, textAlign: 'center', color: '#1f7a36', fontWeight: 700 }}>
                                ✓ {t('Nema chargeback exceptiona — sve čisto', 'No chargeback exceptions — all clean')}
                            </div>
                        ) : (
                            <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                                <table className="admin-user-table" style={{ width: '100%', fontSize: '0.84rem' }}>
                                    <thead><tr>
                                        <th>{t('Datum', 'Date')}</th>
                                        <th>{t('ARN', 'ARN')}</th>
                                        <th>{t('Case', 'Case')}</th>
                                        <th>{t('Terminal/ATM', 'Terminal/ATM')}</th>
                                        <th>{t('Brand', 'Brand')}</th>
                                        <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                        <th>{t('Status', 'Status')}</th>
                                        <th>{t('Akcija', 'Action')}</th>
                                    </tr></thead>
                                    <tbody>
                                        {chargebacksData.rows.map((cb) => (
                                            <tr key={cb.id} style={{ background: cb.status === 'open' ? 'rgba(177,64,64,0.05)' : (cb.status === 'resolved' ? 'rgba(31,122,54,0.04)' : 'transparent') }}>
                                                <td style={{ fontFamily: 'monospace' }}>{fmtDate(cb.exception_date)}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{cb.acquirer_ref_nr ?? '—'}</td>
                                                <td>{cb.case_number ?? '—'}</td>
                                                <td>{cb.atm_name ?? cb.terminal_code ?? '—'}</td>
                                                <td>{cb.card_assoc ?? '—'}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{cb.txn_amount !== null ? eur(cb.txn_amount, 2) : '—'} {cb.txn_cur ?? ''}</td>
                                                <td>
                                                    <span style={{ background: cb.status === 'open' ? '#b14040' : cb.status === 'resolved' ? '#1f7a36' : '#888', color: '#fff', padding: '1px 6px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700 }}>{cb.status}</span>
                                                </td>
                                                <td>
                                                    {cb.status === 'open' ? (
                                                        <button type="button" onClick={() => void resolveChargeback(cb.id)} style={{ padding: '2px 8px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>✓ {t('Resolve', 'Resolve')}</button>
                                                    ) : (cb.resolved_note ? <span title={cb.resolved_note} style={{ fontSize: '0.7rem', color: '#888' }}>{t('napomena…', 'note…')}</span> : null)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ) : <p className="muted">{t('Učitavam…', 'Loading…')}</p>}
                </>
            ) : null}

            {/* Tab: FTT */}
            {tab === 'ftt' && data && !empty ? (
                <>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                        {tile(t('Transakcija', 'Transactions'), num(ftt?.summary.count ?? 0))}
                        {tile(t('Namireno', 'Settled'), num(ftt?.summary.settled ?? 0), undefined, '#1f7a36')}
                        {tile(t('Odbijeno', 'Declined'), num(ftt?.summary.declined ?? 0), undefined, '#b14040')}
                        {tile(t('Marža (EUR)', 'Margin (EUR)'), eur(ftt?.summary.total_margin_eur ?? 0, 2), undefined, GOLD)}
                    </div>
                    <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                        <table className="admin-user-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                            <thead><tr>
                                <th>{t('Vrijeme', 'Time')}</th><th>{t('Terminal', 'Terminal')}</th><th>{t('Brand', 'Brand')}</th><th>{t('Zemlja', 'Country')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th><th style={{ textAlign: 'right' }}>{t('Protuvrijednost', 'Contra')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Marža €', 'Margin €')}</th><th>{t('Status', 'Status')}</th>
                            </tr></thead>
                            <tbody>
                                {(ftt?.recent ?? []).map((r, i) => (
                                    <tr key={i}>
                                        <td style={{ whiteSpace: 'nowrap' }}>{r.txn_at ?? '—'}</td>
                                        <td>{r.terminal_id}</td>
                                        <td>{r.brand}</td>
                                        <td>{r.country}</td>
                                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{num(r.amount, 2)} {r.currency}</td>
                                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{num(r.contra_amount, 2)} {r.contra_ccy}</td>
                                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{r.margin_eur !== null ? num(r.margin_eur, 2) : '—'}</td>
                                        <td><span style={{ color: statusColor(r.status), fontWeight: 600 }}>{r.status}</span></td>
                                    </tr>
                                ))}
                                {(ftt?.recent ?? []).length === 0 ? <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 12 }}>{t('Nema transakcija.', 'No transactions.')}</td></tr> : null}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}

            <p className="muted" style={{ fontSize: '0.74rem', marginTop: 14, fontStyle: 'italic' }}>
                {data ? <>{t('Izvor', 'Source')}: Revenue {num(data.meta.revenue_rows)} · Settlement {num(data.meta.settlement_rows)} · ASP17 {num(data.meta.asp17_rows ?? 0)} · ASP21 {num(data.meta.asp21_rows ?? 0)} · True-up {num(data.meta.trueup_rows ?? 0)} · FTT {num(data.meta.ftt_rows)} {t('redaka', 'rows')}.</> : null}
            </p>
        </section>
    );
}
